import * as Sentry from "@sentry/react";
import { authLogger } from './logger';
import { trackApiError } from './sentry-breadcrumbs';

export const apiVersion = "v1";
export const API_MAX_RETRIES = 2; // configurable cap for network/5xx retries per request

export const getCookie = (name: string): string | null => {
  if (typeof document === 'undefined') return null;
  const cookies = document.cookie ? document.cookie.split('; ') : [];
  for (const c of cookies) {
    const [k, ...rest] = c.split('=');
    if (decodeURIComponent(k) === name) {
      return decodeURIComponent(rest.join('='));
    }
  }
  return null;
};

export const hasAuthCookies = (): boolean => {
  // Treat presence of either token as a valid session-in-progress to avoid
  // force re-exchange (ensureAdminSession) when refresh exists but access is missing.
  const access = getCookie('accessToken');
  const refresh = getCookie('refreshToken');
  return access != null || refresh != null;
};

export const deleteCookie = (name: string): void => {
  if (typeof document === 'undefined' || typeof window === 'undefined') return;
  const host = window.location.hostname;
  const domainAttr = isGetLostDomainHost(host) ? '; Domain=.getlost.me' : '';
  const pathAttr = '; Path=/';
  const expiresPast = '; Expires=Thu, 01 Jan 1970 00:00:00 GMT';

  const k = encodeURIComponent(name);
  // Try Max-Age and Expires variants, with and without domain
  document.cookie = `${k}=; Max-Age=0${pathAttr}${domainAttr}`;
  document.cookie = `${k}=; Max-Age=0${pathAttr}`;
  document.cookie = `${k}=; ${expiresPast}${pathAttr}${domainAttr}`;
  document.cookie = `${k}=; ${expiresPast}${pathAttr}`;
};

const doFetch = (url: string, options: RequestInit = {}) => fetch(url, options);

// Build absolute URL from base and a relative endpoint
const buildUrl = (endpoint: string): string => {
  const base = String((import.meta as any).env?.VITE_API ?? '');
  const cleanBase = base.endsWith('/') ? base : `${base}/`;
  const cleanEndpoint = String(endpoint).replace(/^\//, '');
  return `${cleanBase}${cleanEndpoint}`;
};

// In-flight request dedupe by key (URL+method+body by default)
const inflight = new Map<string, Promise<Response>>();
const requestKey = (url: string, init?: RequestInit) => {
  const method = (init?.method || 'GET').toUpperCase();
  const body = typeof init?.body === 'string' ? init?.body : '';
  return `${method} ${url} ${body}`;
};

// Track an in-flight refresh to avoid racing multiple refresh calls
let refreshing: Promise<boolean> | null = null;

const getAccessExpiry = (): number | null => {
  if (typeof window === 'undefined') return null;
  try {
    const v = window.localStorage.getItem('accessTokenExpiresAt');
    return v ? parseInt(v, 10) : null;
  } catch {
    return null;
  }
};

const shouldRefreshPreemptively = (): boolean => {
  const access = getCookie('accessToken');
  const refresh = getCookie('refreshToken');
  
  // If we have no access token but have a refresh token, we should refresh
  if (!access && refresh) {
    return true;
  }
  
  // If we have both tokens, check expiry
  if (access && refresh) {
    const expiry = getAccessExpiry();
    if (!expiry) return false;
    
    const bufferMs = 5 * 60 * 1000; // 5 minutes before expiry
    return Date.now() >= (expiry - bufferMs);
  }
  
  // No refresh token available
  return false;
};

const preemptiveRefresh = async (): Promise<void> => {
  if (!shouldRefreshPreemptively()) return;
  
  if (!refreshing) {
    const access = getCookie('accessToken');
    const trigger = !access ? 'missing_access_token' : 'token_expiry_approaching';
    authLogger.log('preemptive_refresh', { trigger });
    refreshing = refreshWithCookie().finally(() => {
      refreshing = null;
    });
  }
  
  try {
    await refreshing;
  } catch (error) {
    authLogger.log('refresh_failed', { type: 'preemptive' }, error as Error);
  }
};

const setAccessExpiryFromSeconds = (expiresIn?: number) => {
  if (typeof window === 'undefined') return;
  if (typeof expiresIn !== 'number') return;
  const bufferSec = 60;
  const ts = Date.now() + Math.max(0, expiresIn - bufferSec) * 1000;
  try {
    window.localStorage.setItem('accessTokenExpiresAt', String(ts));
  } catch {}
};

// Attempt to refresh tokens using the refresh token cookie via POST `${API}/refresh/`
const refreshWithCookie = async (): Promise<boolean> => {
  try {
    const refreshToken = getCookie('refreshToken');
    if (!refreshToken) {
      authLogger.log('refresh_failed', { reason: 'no_refresh_token' });
      return false;
    }

    // Check refresh token age for security
    const refreshTokenAge = getCookie('refreshTokenIssuedAt');
    const maxAge = 30 * 24 * 60 * 60 * 1000; // 30 days in milliseconds
    if (refreshTokenAge && (Date.now() - parseInt(refreshTokenAge)) > maxAge) {
      authLogger.log('refresh_failed', { reason: 'refresh_token_expired_by_age' });
      return false;
    }

    authLogger.log('refresh_start', { hasRefreshToken: !!refreshToken });

    const url = buildUrl(`/admin/${apiVersion}/refresh/`);
    // Include Authorization if we have an access token; some backends require it
    // const access = getCookie('accessToken');
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    // if (access) headers['Authorization'] = `Bearer ${access}`;
    const resp = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify({ refresh_token: refreshToken }),
    });

    if (!resp.ok) {
      const errorText = await resp.text().catch(() => '');
      authLogger.log('refresh_failed', { status: resp.status, error: errorText });
      return false;
    }

    const data = await resp.json().catch(() => ({} as any));
    const newAccess = data?.access_token as string | undefined;
    const newRefresh = data?.refresh_token as string | undefined;
    
    if (!newAccess) {
      authLogger.log('refresh_failed', { reason: 'no_access_token_in_response' });
      return false;
    }

    // Handle refresh token rotation (some backends return new refresh token)
    const refreshToUse = newRefresh || refreshToken; // Use new if provided, otherwise keep existing

    // Replace cookies atomically with proper rotation handling
    deleteCookie('accessToken');
    deleteCookie('refreshToken');
    deleteCookie('refreshTokenIssuedAt');
    setCookiesForWildcardDomain({ 
      accessToken: newAccess, 
      refreshToken: refreshToUse,
      refreshTokenIssuedAt: newRefresh ? String(Date.now()) : (getCookie('refreshTokenIssuedAt') || String(Date.now()))
    });
    
    // Update stored expiry if provided
    if (typeof data?.expires_in === 'number') {
      setAccessExpiryFromSeconds(data.expires_in);
    }

    // Log token rotation if new refresh token provided
    if (newRefresh && newRefresh !== refreshToken) {
      authLogger.log('refresh_success', { 
        rotatedRefreshToken: true,
        expiresIn: data?.expires_in 
      });
    } else {
      authLogger.log('refresh_success', { 
        rotatedRefreshToken: false,
        expiresIn: data?.expires_in 
      });
    }
    
    return true;
  } catch (error) {
    authLogger.log('refresh_failed', { type: 'exception' }, error as Error);
    return false;
  }
};

// Build headers consistently and attach Authorization and Content-Type when needed
const buildHeaders = (options: RequestInit): Headers => {
  const token = typeof document !== 'undefined' ? getCookie('accessToken') : null;
  const headers = new Headers(options.headers as HeadersInit | undefined);
  if (token && !headers.has('Authorization')) headers.set('Authorization', `Bearer ${token}`);
  const hasBody = options.body !== undefined && options.body !== null;
  if (hasBody && !headers.has('Content-Type')) headers.set('Content-Type', 'application/json');
  return headers;
};

export const api = async (endpoint: string, options: RequestInit = {}, _attempt = 0) => {
  // Check for preemptive refresh before making request
  await preemptiveRefresh();
  
  const url = buildUrl(endpoint);
  const config: RequestInit = { ...options, headers: buildHeaders(options) };
  const key = requestKey(url, config);

  const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

  const exec = async (): Promise<Response> => {
    let attempt = 0;
    let hasRefreshed = false;
    const startTime = Date.now();
    
    while (true) {
      try {
        const resp = await doFetch(url, attempt === 0 ? config : { ...options, headers: buildHeaders(options) });
        const duration = Date.now() - startTime;
        
        // Add Sentry breadcrumb for API call
        Sentry.addBreadcrumb({
          category: "api",
          level: resp.ok ? "info" : "warning",
          message: `${config.method || "GET"} ${endpoint}`,
          data: {
            endpoint,
            method: config.method || "GET",
            status: resp.status,
            duration,
          },
        });
        
        // Track API errors for Sentry
        if (!resp.ok) {
          trackApiError(endpoint, resp.status, {
            duration,
            method: config.method || "GET",
          });
          
          // Capture errors for critical endpoints
          if (resp.status >= 500) {
            Sentry.captureException(new Error(`API ${resp.status}: ${endpoint}`), {
              tags: {
                endpoint,
                status: resp.status.toString(),
                priority: "critical",
              },
            });
          }
        }
        
        // Handle 401 once via refresh using race condition prevention
        if (resp.status === 401 && !hasRefreshed) {
          // Use existing global refreshing promise to prevent race conditions
          if (!refreshing) {
            refreshing = refreshWithCookie().finally(() => {
              refreshing = null;
            });
          }
          const refreshed = await refreshing;
          hasRefreshed = true;
          if (refreshed) {
            // retry immediately after refresh without counting as a backoff attempt
            continue;
          }
          // If refresh failed, return original 401
          return resp;
        }
        // Retry on 5xx up to API_MAX_RETRIES (disabled in dev mode)
        if (resp.status >= 500 && attempt < API_MAX_RETRIES && !import.meta.env.DEV) {
          attempt += 1;
          const backoff = Math.min(3000, 250 * Math.pow(2, attempt - 1));
          await sleep(backoff);
          continue;
        }
        return resp;
      } catch (err) {
        const duration = Date.now() - startTime;
        
        // Add Sentry breadcrumb for network error
        Sentry.addBreadcrumb({
          category: "api",
          level: "error",
          message: `${config.method || "GET"} ${endpoint} - Network Error`,
          data: {
            endpoint,
            method: config.method || "GET",
            error: err instanceof Error ? err.message : String(err),
            duration,
          },
        });
        
        // Capture network errors for critical endpoints
        Sentry.captureException(err, {
          tags: {
            endpoint,
            type: "network_error",
            priority: "critical",
          },
        });
        
        // Network or fetch error, retry up to API_MAX_RETRIES (disabled in dev mode)
        if (attempt < API_MAX_RETRIES && !import.meta.env.DEV) {
          attempt += 1;
          const backoff = Math.min(3000, 250 * Math.pow(2, attempt - 1));
          await sleep(backoff);
          continue;
        }
        throw err;
      }
    }
  };

  // Deduplicate by key
  const existing = inflight.get(key);
  if (existing) return existing;
  const p = exec().finally(() => inflight.delete(key));
  inflight.set(key, p);
  return p;
};

export const apiJson = async <T = unknown>(endpoint: string, options: RequestInit = {}): Promise<T> => {
  try {
    const resp = await api(endpoint, options);
    if (!resp.ok) {
      // Try to parse a JSON body so we can attach structured fields
      // (status, needsOnboarding) to the thrown Error. Downstream
      // consumers (verifyAdminSession, handleCheckAuth) match on
      // error.status === 404 / error.needsOnboarding === true — see
      // getlst/getlost-admin#115 follow-up.
      let bodyText = "";
      let parsed: any = null;
      try {
        bodyText = await resp.clone().text().catch(() => "");
        if (bodyText) {
          try {
            parsed = JSON.parse(bodyText);
          } catch {
            parsed = null;
          }
        }
      } catch {
        // ignore
      }
      const errorMessage = `${resp.status} ${resp.statusText}${bodyText ? `: ${bodyText}` : ""}`;
      const error: any = new Error(errorMessage);
      error.status = resp.status;
      error.statusText = resp.statusText;
      if (parsed && typeof parsed === "object") {
        if (parsed.needsOnboarding === true) error.needsOnboarding = true;
        if (parsed.error) error.serverMessage = String(parsed.error);
      }

      // Capture JSON API errors
      Sentry.captureException(error, {
        tags: {
          endpoint,
          status: resp.status.toString(),
          priority: resp.status >= 500 ? "critical" : "normal",
        },
      });

      throw error;
    }
    return (await resp.clone().json()) as T;
  } catch (err) {
    // Capture JSON parsing or other errors
    Sentry.captureException(err, {
      tags: {
        endpoint,
        priority: "normal",
      },
    });
    throw err;
  }
};

// A convenience JSON requester that dedupes in-flight requests by URL + method + body
export const apiJsonDedup = async <T = unknown>(endpoint: string, options: RequestInit = {}): Promise<T> => {
  return apiJson<T>(endpoint, options);
};

// Set one or more cookies scoped to *.getlost.me (with dev fallbacks)
export const setCookiesForWildcardDomain = (values: Record<string, string>): void => {
  if (typeof document === 'undefined' || typeof window === 'undefined') return;

  const host = window.location.hostname;
  const domainAttr = isGetLostDomainHost(host) ? '; Domain=.getlost.me' : '';

  const isHttps = window.location.protocol === 'https:';
  const secureAttr = isHttps ? '; Secure' : '';
  const sameSiteAttr = '; SameSite=Lax';
  const pathAttr = '; Path=/';

  Object.entries(values).forEach(([key, val]) => {
    const k = encodeURIComponent(key);
    const v = encodeURIComponent(val);
    document.cookie = `${k}=${v}${pathAttr}${domainAttr}${secureAttr}${sameSiteAttr}`;
  });
};

// Central host check for cookie domain logic
const isGetLostDomainHost = (host: string): boolean =>
  host === 'getlost.me' || host.endsWith('.getlost.me');
